import { css, SerializedStyles } from '@emotion/react';
import {
  AbilityOutput,
  AbilityOutputMultiplierType,
  AbilityUpgradeCondition,
  areAbilityUpgradeConditionsEqual,
  BaseCardAbility,
  BaseCountersAbility,
  CardAdditionalCosts,
  DraftCardAbility,
  EachTurnAbility,
  EndOfGameAbility,
  GainOtherCardAbility,
  GainSelfAbility,
  getAdditionalCosts,
  getAllCardAbilities,
  getGainSelfCardAbility,
} from '@mythos/game/CardAbilities';
import {
  CardAffinity,
  CardType,
  isCardAffinity,
  isCardType,
} from '@mythos/game/CardTypes';
import { CardDef } from '@mythos/game/GameModel';
import { Token } from '@mythos/game/Resources';
import { CardWithID } from '@mythos/game/Rules';
import { HoverCardRenderer } from 'HoverCardStore';
import invariant from 'invariant';
import { times } from 'lodash';
import nullthrows from 'nullthrows';
import React, { ReactElement } from 'react';
import { match, P } from 'ts-pattern';
import FooterGrunge1 from '../assets/card_frame/footer_grunge_1.svg';
import CardFrameStandard from '../assets/card_frame/frame_standard.svg';
import CardFrameUpgrade from '../assets/card_frame/frame_upgrade.svg';
import TitlePatternBasic from '../assets/card_frame/title_pattern_overlay_basic2.svg';
import TitlePatternDerion from '../assets/card_frame/title_pattern_overlay_derion.svg';
import TitlePatternLeader from '../assets/card_frame/title_pattern_overlay_leader.svg';
import TitlePatternTyros from '../assets/card_frame/title_pattern_overlay_tyros.svg';
import TitlePatternZeona from '../assets/card_frame/title_pattern_overlay_zeona.svg';

import { ArtResolutionContext, cardArtUrl } from './ArtResolution';
import BaseCardView, {
  CARD_BLEED_WIDTH,
  CARD_BORDER_RADIUS,
  CARD_BORDER_WIDTH,
} from './BaseCardView';
import CardBanner from './CardBanner';
import {
  AffinityOrTypeCharToProphecySymbolImage,
  EffectPill,
  EffectTriggerPrefix,
  GainOtherTriggerPrefix,
  getCornerDecorationAssetURL,
  getTrayDecorationAssetURL,
  SymbolWithEmbeddedSymbol,
  SymbolWithNumber,
} from './CardHelpers';
import { CardAffinityToFrameColor } from './ColorPalette';
import Symbols, {
  BaseResourceToSymbol,
  CardAffinityToSymbol,
  CardTypeToSymbol,
  ResourceToSymbol,
  SymbolView,
} from './Symbols';

// Placeholder symbols for generic Basic reference cards.
// BASIC_COST_SYMBOL replaces the numeric cost ("X") on generic Mercenary cards.
// BASIC_VARIABLE_SYMBOL replaces the variable-gain marker ("?") on generic
// Scavenge/Mercenary cards. Tweak these to iterate on the look.
export const BASIC_COST_SYMBOL = 'χ';
export const BASIC_VARIABLE_SYMBOL = 'δ';
// Shared font weight for the generic Basic placeholder symbols (medium).
export const BASIC_SYMBOL_FONT_WEIGHT = 500;
// Vertical nudge (px, positive = down) for the cost glyph, which sits high in
// the coin because the Greek fallback glyph's metrics differ from the title font.
export const BASIC_COST_Y_OFFSET = -1.5;

export type CardEventFunc<TCard> = (
  card: TCard,
  event: React.MouseEvent,
) => void;
export type CardRenderer = (props: Props) => ReactElement;

export type GenericGainSelfType = 'mercenary' | 'scavenge';

export type CardViewOverrideOptions = {
  genericCost?: boolean;
  genericGainSelf?: GenericGainSelfType;
};

interface Props<TCard extends CardDef = CardDef> {
  onCardEnter?: CardEventFunc<TCard>;
  onCardLeave?: CardEventFunc<TCard>;
  onLeftClick?: CardEventFunc<TCard>;
  onRightClick?: CardEventFunc<TCard>;

  card: TCard;

  overlayStyle?: React.CSSProperties;
  overlayChildren?: React.ReactNode;
  overlayCSS?: SerializedStyles;

  mini?: boolean;

  /** Override options for rendering generic card variants */
  overrides?: CardViewOverrideOptions;

  containerStyles?: {
    frame?: SerializedStyles;
    title?: SerializedStyles;
    cost?: SerializedStyles;
    obtain?: SerializedStyles;
    effect?: SerializedStyles;
    type?: SerializedStyles;
    affinity?: SerializedStyles;
    baseFavor?: SerializedStyles;
    prophecy?: SerializedStyles;
  };
}
export type CardContainerStyles = Props['containerStyles'];

const CardView = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { card, overlayStyle, overlayCSS, overlayChildren, mini, overrides } =
    props;

  const handleMouseEnter = (e: React.MouseEvent) => {
    props.onCardEnter && props.onCardEnter(card, e);
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    props.onCardLeave && props.onCardLeave(card, e);
  };

  const handleLeftClick = (e: React.MouseEvent) => {
    props.onLeftClick && props.onLeftClick(card, e);
  };

  const handleRightClick = (e: React.MouseEvent) => {
    if (props.onRightClick) {
      e.preventDefault();
      props.onRightClick(card, e);
    }
  };
  const artResolution = React.useContext(ArtResolutionContext);
  const art = card.imageName && cardArtUrl(card.imageName, artResolution);

  let underlay_style: React.CSSProperties = {
    backgroundImage: (art && `url(${art})`) || undefined,
    backgroundSize: 'contain',
    borderRadius: CARD_BORDER_RADIUS,
  };
  if (card.imageName && XXX_ARTS_TO_SHIFT_DOWN_XXX.includes(card.imageName)) {
    underlay_style.backgroundSize = 'cover';
    underlay_style.backgroundPositionY = 12;
  }
  let card_style: React.CSSProperties = {};

  let titleView = (
    <div css={CardStyle.titleContainer}>
      <div css={[CardStyle.titleCost, props.containerStyles?.cost]}>
        <SymbolWithNumber
          symbol={Symbols.GOLD}
          number={overrides?.genericCost ? BASIC_COST_SYMBOL : card.cost}
          numberFontFamily={overrides?.genericCost ? 'inherit' : undefined}
          numberFontWeight={
            overrides?.genericCost ? BASIC_SYMBOL_FONT_WEIGHT : undefined
          }
          numberYOffset={
            overrides?.genericCost ? BASIC_COST_Y_OFFSET : undefined
          }
          height={16}
          fontSize={9}
          style={{
            marginRight: 2,
            marginLeft: 3,
          }}
        />
      </div>
      <div css={CardStyle.titleCardName}>{card.name.toUpperCase()}</div>
    </div>
  );
  const cardEffectsViewModel = computeCardEffectRows(card);
  const isUpgrade = cardEffectsViewModel.upgradeCondition !== null;
  const frameFooterColor = CardAffinityToFrameColor[card.affinity];
  const trayDecorationURL = getTrayDecorationAssetURL(
    card.type,
    card.affinity,
    isUpgrade,
  );
  const trayDecoration = trayDecorationURL ? (
    <div
      css={CardStyle.fullFrameOverlayWithBleed}
      style={{
        backgroundImage: `url(${trayDecorationURL})`,
      }}
    />
  ) : null;
  const cornerDecorationURL = getCornerDecorationAssetURL(card.affinity);
  const cornerDecoration = cornerDecorationURL ? (
    <div
      css={CardStyle.fullFrameOverlayWithBleed}
      style={{
        backgroundImage: `url(${cornerDecorationURL})`,
      }}
    />
  ) : null;
  const cardFrame = (
    <>
      <div
        css={CardStyle.frameFooterBackground}
        style={{
          height: isUpgrade ? 39 : 29,
          backgroundColor: frameFooterColor,
        }}
      />
      {trayDecoration}
      {cornerDecoration}
      <div
        style={{
          backgroundImage: `url(${isUpgrade ? CardFrameUpgrade : CardFrameStandard})`,
        }}
        css={CardStyle.fullFrameOverlayWithBleed}
      />
      <div
        css={CardStyle.fullFrameOverlayWithBleed}
        style={{
          backgroundImage: `url(${nullthrows(AffinityToTitlePatternAsset[card.affinity])})`,
        }}
      />
    </>
  );

  return (
    <BaseCardView
      ref={ref}
      mini={mini}
      cardStyle={card_style}
      underlayStyle={underlay_style}
      overlayStyle={overlayStyle}
      overlayCSS={overlayCSS}
      overlayChildren={overlayChildren}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onLeftClick={handleLeftClick}
      onRightClick={handleRightClick}
      css={props.containerStyles?.frame}
    >
      {cardFrame}
      {titleView}
      {overrides?.genericGainSelf ? (
        <GenericGainSelfEffect
          css={[GainSelfEffectPositionStyle, props.containerStyles?.obtain]}
          borderColor={CardAffinityToFrameColor[card.affinity]}
          type={overrides.genericGainSelf}
        />
      ) : (
        <GainSelfEffect
          css={[GainSelfEffectPositionStyle, props.containerStyles?.obtain]}
          ability={getGainSelfCardAbility(card)}
          borderColor={CardAffinityToFrameColor[card.affinity]}
          backgroundColor="rgba(255, 255, 255, 0.1)"
        />
      )}
      <ExtraCostsView
        borderColor={CardAffinityToFrameColor[card.affinity]}
        additionalCosts={getAdditionalCosts(card)}
      />

      <CardFooter
        card={card}
        cardEffectsViewModel={cardEffectsViewModel}
        typeContainerStyle={props.containerStyles?.type}
        affinityContainerStyle={props.containerStyles?.affinity}
        baseFavorContainerStyle={props.containerStyles?.baseFavor}
        prophecyContainerStyle={props.containerStyles?.prophecy}
        effectContainerStyle={props.containerStyles?.effect}
      />
    </BaseCardView>
  );
});
export default CardView;

export function BasicCardRenderer(props: Props) {
  return <CardView {...props} />;
}
export function makeMultiCardRenderer(cardsByID: {
  [key: string]: CardWithID;
}): HoverCardRenderer {
  return (props: { card: string[] }) => {
    const cardIDs = props.card;
    return <MultiCardDefRenderer card={cardIDs.map((id) => cardsByID[id])} />;
  };
}
export const MultiCardDefRenderer = (props: { card: CardWithID[] }) => {
  return (
    <div css={MultiCardContainerStyle}>
      {props.card.map((card) => {
        return (
          <div key={card.id} css={MultiCardCardContainerStyle}>
            <CardView card={card} />
          </div>
        );
      })}
    </div>
  );
};
const MultiCardContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});
const MultiCardCardContainerStyle = css({
  zoom: 0.5,
});

function CardFooterAffinityView(props: {
  card: CardDef;
  affinityContainerStyle?: SerializedStyles;
}) {
  if (props.card.affinity === CardAffinity.None) {
    return null;
  }

  const symbol = CardAffinityToSymbol[props.card.affinity];
  return (
    <div
      css={[
        CardStyle.footerCardAffinityContainer,
        props.affinityContainerStyle,
      ]}
    >
      <SymbolView symbol={symbol} css={CardStyle.footerCardAffinity} />
    </div>
  );
}
function CardFooterTypeView(props: {
  card: CardDef;
  typeContainerStyle?: SerializedStyles;
}) {
  const symbol = CardTypeToSymbol[props.card.type];
  return (
    <div css={[CardStyle.footerCardTypeContainer, props.typeContainerStyle]}>
      <SymbolView symbol={symbol} css={CardStyle.footerCardType} />
    </div>
  );
}

function CardFooterBaseFavorView(props: {
  card: CardDef;
  baseFavorContainerStyle?: SerializedStyles;
}) {
  if (props.card.favor === 0) {
    return null;
  }
  const favorSymbol =
    props.card.favor > 9
      ? Symbols.FAVOR_T4
      : props.card.favor > 6
        ? Symbols.FAVOR_T3
        : props.card.favor > 3
          ? Symbols.FAVOR_T2
          : Symbols.FAVOR_T1;
  return (
    <div css={[FavorStyles.baseFavorContainer, props.baseFavorContainerStyle]}>
      {props.card.favor > 0 && (
        <SymbolWithNumber
          symbol={favorSymbol}
          number={props.card.favor}
          height={11}
          fontSize={6}
          css={props.baseFavorContainerStyle}
          style={{
            padding: 1,
            color: 'black',
          }}
        />
      )}
    </div>
  );
}

function CardFooterProphecyFavorView(props: {
  card: CardDef;
  prophecyContainerStyle?: SerializedStyles;
}) {
  const prophecyEffectSymbols = getAllCardAbilities(props.card)
    .filter((ability) => ability instanceof EndOfGameAbility)
    .map((ability, i) =>
      ability.outputs.map((output, j) => {
        const prophecyMultiplierType = output.multiplier?.resource;
        invariant(prophecyMultiplierType, 'expected prophecy multiplier type');
        const prophecySymbol = nullthrows(
          AffinityOrTypeCharToProphecySymbolImage[prophecyMultiplierType],
        );
        return (
          <React.Fragment key={`${i}-${j}`}>
            {times(output.outputRatio, (k) => {
              return (
                <SymbolView
                  key={`${i}-${j}-${k}`}
                  symbol={prophecySymbol}
                  css={FavorStyles.prophecyTypeSymbol}
                />
              );
            })}
          </React.Fragment>
        );
      }),
    );
  if (prophecyEffectSymbols.length === 0) {
    return null;
  }
  return (
    <div css={[FavorStyles.prophecyContainer, props.prophecyContainerStyle]}>
      {prophecyEffectSymbols}
    </div>
  );
}

function CardFooter(props: {
  card: CardDef;
  cardEffectsViewModel: CardEffectRowsViewModel;
  effectContainerStyle?: SerializedStyles;
  typeContainerStyle?: SerializedStyles;
  affinityContainerStyle?: SerializedStyles;
  baseFavorContainerStyle?: SerializedStyles;
  prophecyContainerStyle?: SerializedStyles;
}) {
  const { card, cardEffectsViewModel } = props;
  return (
    <div css={CardStyle.footer}>
      {/* TODO this needs to be clipped to the effect area */}
      <div css={CardStyle.footerGrunge} />
      <CardEffects
        card={card}
        cardEffectsViewModel={cardEffectsViewModel}
        effectContainerStyle={props.effectContainerStyle}
      />

      <CardFooterAffinityView
        card={card}
        affinityContainerStyle={props.affinityContainerStyle}
      />
      <CardFooterTypeView
        card={card}
        typeContainerStyle={props.typeContainerStyle}
      />

      <CardFooterBaseFavorView
        card={props.card}
        baseFavorContainerStyle={props.baseFavorContainerStyle}
      />
      <CardFooterProphecyFavorView
        card={props.card}
        prophecyContainerStyle={props.prophecyContainerStyle}
      />
    </div>
  );
}

// XXX: change these assets when we have them
const AffinityToTitlePatternAsset = {
  [CardAffinity.Derion]: TitlePatternDerion,
  [CardAffinity.Tyros]: TitlePatternTyros,
  [CardAffinity.Zeona]: TitlePatternZeona,
  [CardAffinity.Basic]: TitlePatternBasic,
  [CardAffinity.Leader]: TitlePatternLeader,
  [CardAffinity.None]: null,
};

const CardStyle = {
  titleContainer: css({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
  }),
  titleCardName: css({
    position: 'absolute',
    top: 3,
    left: 20,

    color: 'white',
    fontFamily: 'Mythos-CardTitle',
    fontSize: 6,
  }),
  titleCost: css({
    position: 'absolute',
    top: -2,
    left: -1,
  }),
  fullFrameOverlay: css({
    position: 'absolute',
    top: -CARD_BORDER_WIDTH,
    left: -CARD_BORDER_WIDTH,
    right: -CARD_BORDER_WIDTH,
    bottom: -CARD_BORDER_WIDTH,
    borderRadius: CARD_BORDER_RADIUS,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  }),
  fullFrameOverlayWithBleed: css({
    position: 'absolute',
    top: -CARD_BORDER_WIDTH - CARD_BLEED_WIDTH,
    left: -CARD_BORDER_WIDTH - CARD_BLEED_WIDTH,
    right: -CARD_BORDER_WIDTH - CARD_BLEED_WIDTH,
    bottom: -CARD_BORDER_WIDTH - CARD_BLEED_WIDTH,
    borderRadius: CARD_BORDER_RADIUS,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  }),
  frameFooterBackground: css({
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  }),
  footer: css({
    bottom: 0,

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }),
  footerGrunge: css({
    backgroundImage: `url(${FooterGrunge1})`,
    backgroundSize: 'cover',
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: '100%',
    height: 44,
  }),
  footerCardAffinityContainer: css({
    position: 'absolute',
    transform: 'translate(-50%, 50%)',
    right: 0,
    bottom: 20,

    // backgroundColor: 'rgba(255, 0, 0, 1)',
  }),
  footerCardTypeContainer: css({
    position: 'absolute',
    transform: 'translate(-50%, 50%)',
    right: 0,
    bottom: 5.5,
    // backgroundColor: 'rgba(255, 0, 0, 1)',
  }),
  footerCardAffinity: css({
    width: 11,
    height: 'auto',
  }),
  footerCardType: css({
    width: 11,
    height: 'auto',
  }),
  footerUpgradeConditionContainer: css({
    position: 'absolute',
    bottom: 19.5,
    left: '50%',
    transform: 'translate(-50%, 50%)',
  }),
  footerStandardEffectsContainer: css({
    position: 'absolute',
    bottom: 13.5,
    left: '50%',
    transform: 'translate(-50%, 50%)',
    // maxWidth: 35,
  }),
  footerUnupgradedEffectsContainer: css({
    position: 'absolute',
    bottom: 7,
    left: '50%',
    transform: 'translate(-50%, 50%)',
  }),
  footerUpgradeEffectsContainer: css({
    position: 'absolute',
    bottom: 32,
    left: '50%',
    transform: 'translate(-50%, 50%)',
  }),
} as const;

const EffectContainerHighlightStyles = {
  standard: css({
    position: 'absolute',
    bottom: -2,
    left: '20%',
    right: '20%',
    height: 29,
  }),
  upgrade: css({
    position: 'absolute',
    bottom: -2,
    left: '20%',
    right: '20%',
    height: 42,
  }),
};

const FavorStyles = {
  baseFavorContainer: css({
    position: 'absolute',
    bottom: 5.5,
    left: 10.5,
    display: 'flex',
    flexDirection: 'row',
    transform: 'translate(-50%, 50%)',
  }),
  prophecyContainer: css({
    position: 'absolute',
    bottom: 20,
    left: 10.5,
    transform: 'translate(-50%, 50%)',

    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  prophecyTypeSymbol: css({
    height: 11,
    width: 'auto',
  }),
  prophecyColon: css({
    marginBottom: 2,
  }),
  thresholdProphecySymbolExtras: css({
    paddingLeft: 1,
  }),
};

function LargeSymbolWithNumber(props: {
  symbol: Symbols;
  number: number;
  className?: string;
}) {
  return (
    <SymbolWithNumber
      className={props.className}
      symbol={props.symbol}
      number={props.number}
      height={12}
    />
  );
}

function ExtraCostsView(props: {
  borderColor: string;
  additionalCosts?: CardAdditionalCosts | null;
}) {
  const { borderColor, additionalCosts } = props;
  if (!additionalCosts) {
    return null;
  }

  return (
    <div css={ExtraCostsStyles.container}>
      {additionalCosts.warTokens > 0 && (
        <CardBanner
          css={ExtraCostsStyles.banner}
          borderColor={borderColor}
          backgroundColor="rgba(210, 210, 210, 0.25)"
          bannerType="left"
        >
          <LargeSymbolWithNumber
            symbol={Symbols.TOKEN_WAR}
            number={additionalCosts.warTokens}
          />
        </CardBanner>
      )}
    </div>
  );
}
const ExtraCostsStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    alignSelf: 'flex-end',

    position: 'absolute',
    top: 0,
    left: -4,
  }),
  banner: css({}),
  pill: css({
    margin: 1,
  }),
};

export function GainSelfEffect(props: {
  className?: string;
  style?: React.CSSProperties;
  ability?: GainSelfAbility | null;
  borderColor: string;
  backgroundColor?: string;
}) {
  const { className, style, ability, borderColor, backgroundColor } = props;
  if (!ability) {
    return null;
  }
  const orderedOutputs = [...ability.outputs];
  return (
    <div
      css={GainSelfEffectStyles.container}
      className={className}
      style={{
        ...style,
      }}
    >
      <CardBanner
        key={`gain-self-effect-banner`}
        css={GainSelfEffectStyles.banner}
        bannerType="top-flag"
        borderColor={borderColor}
        backgroundColor={borderColor}
      >
        <SymbolView
          css={GainSelfEffectStyles.downArrowSymbol}
          symbol={Symbols.OBTAIN}
        />
        {orderedOutputs.map((output, i) => {
          // XXX: move to CardHelpers EffectPill
          if (output.multiplier?.resource) {
            const embeddedSymbol = match(output.multiplier.resource)
              .when(isCardType, (type) => {
                return CardTypeToSymbol[type];
              })
              .when(isCardAffinity, (affinity) => {
                return CardAffinityToSymbol[affinity];
              })
              .otherwise(() => {
                invariant(
                  false,
                  'unsupported multiplier resource for GainSelfEffect',
                );
              });
            return times(output.outputRatio, (j) => (
              <SymbolWithEmbeddedSymbol
                key={j}
                outerSymbol={ResourceToSymbol[output.outputResource]}
                outerSymbolHeight={12}
                embeddedSymbol={embeddedSymbol}
                embeddedSymbolHeight={8}
                embeddedSymbolStyle={{
                  filter: 'brightness(0)',
                }}
              />
            ));
          }
          return (
            <SymbolWithNumber
              key={i}
              symbol={ResourceToSymbol[output.outputResource]}
              number={output.outputRatio}
            />
          );
        })}
      </CardBanner>
    </div>
  );
}

function SymbolWithText(props: { symbol: Symbols; text: string }) {
  return (
    <div css={GenericGainSelfEffectStyles.symbolWithText}>
      <SymbolView
        symbol={props.symbol}
        css={GenericGainSelfEffectStyles.symbol}
      />
      <span css={GenericGainSelfEffectStyles.embeddedText}>{props.text}</span>
    </div>
  );
}

function GenericGainSelfEffect(props: {
  className?: string;
  style?: React.CSSProperties;
  borderColor: string;
  type: GenericGainSelfType;
}) {
  const { className, style, borderColor, type } = props;
  return (
    <div
      css={GainSelfEffectStyles.container}
      className={className}
      style={style}
    >
      <CardBanner
        css={GainSelfEffectStyles.banner}
        bannerType="top-flag"
        borderColor={borderColor}
        backgroundColor={borderColor}
      >
        <SymbolView
          css={GainSelfEffectStyles.downArrowSymbol}
          symbol={Symbols.OBTAIN}
        />
        {type === 'scavenge' && (
          <SymbolWithText symbol={Symbols.GOLD} text={BASIC_VARIABLE_SYMBOL} />
        )}
        <SymbolWithText
          symbol={Symbols.MILITARY}
          text={BASIC_VARIABLE_SYMBOL}
        />
      </CardBanner>
    </div>
  );
}
const GenericGainSelfEffectStyles = {
  symbolWithText: css({
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  symbol: css({
    height: 10,
    width: 'auto',
  }),
  embeddedText: css({
    position: 'absolute',
    fontSize: 7,
    fontWeight: BASIC_SYMBOL_FONT_WEIGHT,
    color: 'black',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  }),
};

const GainSelfEffectPositionStyle = css({
  position: 'absolute',
  top: 11,
  left: 3,
});

const GainSelfEffectStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 14,
    filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.6))',
  }),
  banner: css({
    flexDirection: 'column',
    width: 12,

    paddingTop: 4,
    paddingBottom: 3,
    marginTop: -5,
    '&:last-child': {
      marginTop: 0,
    },
  }),
  downArrowBanner: css({
    position: 'absolute',
    top: -3,
    left: 0,
    boxSizing: 'border-box',
    width: `100%`,
    height: 10,
  }),
  downArrowSymbol: css({
    height: 3,
    width: 'auto',
    paddingTop: 1,
    paddingBottom: 1,
  }),
  pillsContainer: css({
    display: 'flex',
    flexDirection: 'column-reverse',
    alignItems: 'center',
  }),
  pill: css({
    flexDirection: 'column',
    paddingTop: 6,
    paddingBottom: 2,
  }),
};

function CardEffects(props: {
  card: CardDef;
  cardEffectsViewModel: CardEffectRowsViewModel;
  effectContainerStyle?: SerializedStyles;
}) {
  const { standardEffects, upgradeEffects, upgradeCondition } =
    props.cardEffectsViewModel;

  let upgradeConditionView: React.ReactNode | null = null;

  if (upgradeCondition) {
    upgradeConditionView = (
      <div css={CardStyle.footerUpgradeConditionContainer}>
        <UpgradeCondition upgradeCondition={upgradeCondition} />
      </div>
    );
  }

  let standardEffectsView: React.ReactNode | null = null;
  if (standardEffects.length > 0) {
    standardEffectsView = (
      <div
        css={
          upgradeCondition
            ? CardStyle.footerUnupgradedEffectsContainer
            : CardStyle.footerStandardEffectsContainer
        }
      >
        {standardEffects.map((effect, i) => (
          <CardEffect
            key={i}
            ability={effect}
            cardType={props.card.affinity}
            upgradeCondition={null}
          />
        ))}
      </div>
    );
  }

  let upgradeEffectsView: React.ReactNode | null = null;
  if (upgradeEffects.length > 0) {
    upgradeEffectsView = (
      <div css={CardStyle.footerUpgradeEffectsContainer}>
        {upgradeEffects.map((effect, i) => (
          <CardEffect
            key={i}
            ability={effect}
            cardType={props.card.affinity}
            upgradeCondition={upgradeCondition}
          />
        ))}
      </div>
    );
  }

  // Render highlight overlay for effects area when needed
  let highlightView: React.ReactNode | null = null;
  if (props.effectContainerStyle) {
    highlightView = (
      <div
        css={[
          props.effectContainerStyle,
          upgradeCondition
            ? EffectContainerHighlightStyles.upgrade
            : EffectContainerHighlightStyles.standard,
        ]}
      />
    );
  }

  return (
    <>
      {highlightView}
      {upgradeConditionView}
      {standardEffectsView}
      {upgradeEffectsView}
    </>
  );
}

function getUpgradeSymbol(upgradeType: AbilityOutputMultiplierType): Symbols {
  return match(upgradeType)
    .with(CardAffinity.Tyros, () => Symbols.CARD_AFFINITY_TYROS)
    .with(CardAffinity.Derion, () => Symbols.CARD_AFFINITY_DERION)
    .with(CardAffinity.Zeona, () => Symbols.CARD_AFFINITY_ZEONA)
    .with(CardType.Unit, () => Symbols.CARD_TYPE_UNIT)
    .with(CardType.Building, () => Symbols.CARD_TYPE_BUILDING)
    .with(CardType.Event, () => Symbols.CARD_TYPE_EVENT)
    .with(Token.War, () => Symbols.TOKEN_WAR)
    .with('card_set', () => Symbols.CARD_SET)
    .otherwise(() => {
      invariant(false, 'unhandled upgrade type');
    });
}
export function UpgradeCondition(props: {
  className?: string;
  upgradeCondition: AbilityUpgradeCondition;
}) {
  const { className, upgradeCondition } = props;

  const upgradeAmount = upgradeCondition.upgradeAmount;
  const upgradeType = upgradeCondition.upgradeType;
  const upgradeSymbol = getUpgradeSymbol(upgradeType);
  return (
    <div className={className} css={CardEffectStyles.upgradeCondition}>
      <SymbolView
        symbol={Symbols.UPGRADE}
        css={CardEffectStyles.upgradeSymbol}
      />
      {upgradeAmount}
      <SymbolView
        symbol={upgradeSymbol}
        css={CardEffectStyles.upgradeConditionSymbol}
      />
    </div>
  );
}

export function CardEffect(props: {
  ability: BaseCardAbility;
  cardType: CardAffinity;
  upgradeCondition: AbilityUpgradeCondition | null;
  style?: React.CSSProperties;
}): React.ReactNode | null {
  const { ability, upgradeCondition, style } = props;

  let multiresourcePill: React.ReactNode | null = null;
  let containerStyle: React.CSSProperties | undefined = {};
  const abilityOutputs = ability.outputs.filter((o) =>
    areAbilityUpgradeConditionsEqual(
      o.upgradeCondition || null,
      upgradeCondition,
    ),
  );
  if (abilityOutputs.length === 0) {
    return null;
  }

  const prefix = match(ability)
    .with(P.instanceOf(GainSelfAbility), () => {
      invariant(false, 'unhandled GainSelfAbility');
    })
    .with(P.instanceOf(GainOtherCardAbility), (a) => {
      const targetType = a.getTargetType();
      if (targetType.target_type === 'tribute') {
        if (
          abilityOutputs.length === 3 &&
          abilityOutputs.every(
            (o) =>
              o.outputRatio === abilityOutputs[0].outputRatio && !o.multiplier,
          )
        ) {
          multiresourcePill = (
            <EffectPillOrSymbols output={abilityOutputs[0]} />
          );
        }
        return <GainOtherTriggerPrefix targetType={targetType} />;
      } else if (targetType.target_type === 'token') {
        multiresourcePill = (
          <>
            {abilityOutputs.map((output, i) => {
              return <EffectPillOrSymbols key={i} output={output} />;
            })}
          </>
        );
        return <GainOtherTriggerPrefix targetType={targetType} />;
      } else if (targetType.target_type === 'card_type') {
        return <GainOtherTriggerPrefix targetType={targetType} />;
      } else if (targetType.target_type === 'card_affinity') {
        return <GainOtherTriggerPrefix targetType={targetType} />;
      }
    })
    .with(P.instanceOf(EachTurnAbility), (a) => {
      multiresourcePill = (
        <>
          {abilityOutputs.map((output, i) => {
            return <EffectPillOrSymbols key={i} output={output} />;
          })}
        </>
      );
      return null;
    })
    .with(P.instanceOf(DraftCardAbility), (a) => {
      const symbol = match(a.getDraftTarget)
        .with(CardAffinity.Tyros, () => Symbols.M3_DRAFT_TYROS)
        .with(CardAffinity.Derion, () => Symbols.M3_DRAFT_DERION)
        .with(CardAffinity.Zeona, () => Symbols.M3_DRAFT_ZEONA)
        .otherwise(() => {
          console.warn('no symbol for', a.getDraftTarget);
          return null;
        });
      return symbol && <EffectTriggerPrefix symbol={symbol} />;
    })
    .with(P.instanceOf(BaseCountersAbility), () => {
      containerStyle = {
        height: 12,
      };
      multiresourcePill = (
        <>
          {abilityOutputs.map((output, i) => {
            invariant(!output.multiplier, 'unexpected base multiplier');

            return times(output.outputRatio, (j) => (
              <SymbolView
                key={j}
                symbol={nullthrows(BaseResourceToSymbol[output.outputResource])}
                css={CardEffectStyles.baseSymbol}
              />
            ));
          })}
        </>
      );
      return null;
    })
    .with(P.instanceOf(EndOfGameAbility), () => {
      return <EffectTriggerPrefix symbol={Symbols.EOG} />;
    })
    .otherwise(() => {
      console.warn('no prefix for', ability);
      return null;
    });

  const outputs = abilityOutputs;
  const pills =
    multiresourcePill ??
    outputs.map((output, i) => <EffectPill key={i} output={output} />);
  let cardEffect = (
    <div css={[CardEffectStyles.cardEffect]} style={containerStyle}>
      {prefix}
      {pills}
    </div>
  );
  return cardEffect;
}

export type CardEffectRowsViewModel = {
  standardEffects: BaseCardAbility[];
  upgradeEffects: BaseCardAbility[];
  upgradeCondition: AbilityUpgradeCondition | null;
};
export function computeCardEffectRows(card: CardDef): CardEffectRowsViewModel {
  let upgradeCondition: AbilityUpgradeCondition | null = null;

  let standardEffects: BaseCardAbility[] = [];
  let upgradeEffects: BaseCardAbility[] = [];
  for (const ability of getAllCardAbilities(card)) {
    if (ability instanceof EndOfGameAbility) {
      continue; // End of game abilities are handled separately and do not count towards normal effect rows
    }

    let addedStandardEffect = false;
    let addedUpgradeEffect = false;
    for (const output of ability.outputs) {
      const outputUpgradeCondition = output.upgradeCondition;
      if (outputUpgradeCondition) {
        invariant(
          !upgradeCondition ||
            areAbilityUpgradeConditionsEqual(
              upgradeCondition,
              outputUpgradeCondition,
            ),
          'unexpected upgrade condition',
        );
        upgradeCondition = outputUpgradeCondition;
        if (!addedUpgradeEffect) {
          upgradeEffects.push(ability);
          addedUpgradeEffect = true;
        }
        continue;
      }
      if (!addedStandardEffect) {
        standardEffects.push(ability);
        addedStandardEffect = true;
      }
    }
  }

  return {
    standardEffects,
    upgradeEffects,
    upgradeCondition,
  };
}

const CardEffectStyles = {
  upgradeSymbol: css({
    height: 6,
    marginLeft: 1,
    marginRight: 1,
    width: 'auto',
  }),
  upgradeConditionSymbol: css({
    height: 9,
    width: 'auto',
    marginLeft: 1,
  }),
  upgradeCondition: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    fontSize: 7,
    color: 'white',
    width: 'fit-content',
    alignItems: 'center',
  }),
  cardEffect: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: 'fit-content',
    flexWrap: 'wrap',
  }),
  resourceContainer: css({
    display: 'flex',
    flexDirection: 'row',
  }),
  baseSymbol: css({
    height: 11,
    width: 'auto',
  }),
  resourceSymbol: css({
    height: 10,
    width: 'auto',
  }),
};

function EffectPillOrSymbols(props: { output: AbilityOutput }) {
  const output = props.output;
  if (output.multiplier) {
    return <EffectPill output={output} />;
  }
  return (
    <div css={CardEffectStyles.resourceContainer}>
      {times(output.outputRatio, (j) => {
        return (
          <SymbolView
            key={j}
            symbol={ResourceToSymbol[output.outputResource]}
            css={CardEffectStyles.resourceSymbol}
          />
        );
      })}
    </div>
  );
}

const XXX_ARTS_TO_SHIFT_DOWN_XXX: string[] = [
  'ArtLeader',
  'ArtMercenary',
  'ArtMillOfDerion',
  'ArtTrader',
  'ArtPoet',
  'ArtBattleHymn',
];
